import { Action, createReducer, on } from '@ngrx/store';
import {
  CacheVerificationStatus,
  VerificationCacheEntry,
  VERIFICATION_REVERIFY_COOLDOWN_MS,
} from 'src/app/requests/request-verifications/verification-cache/verification-cache.models';
import {
  autoTriggerVerifications,
  hydrateVerificationResults,
  pollFailed,
  pollResultReceived,
  pollTimeout,
  pollVerification,
  resetVerificationCacheForRequest,
  reverifyField,
  triggerVerification,
  verificationFailed,
  verificationResponseReceived,
} from '../../actions/verification-cache/verification-cache.actions';

export interface VerificationCacheState {
  requestId: string | null;
  entries: Record<string, VerificationCacheEntry>;
  detecting: boolean;
}

export const initialState: VerificationCacheState = {
  requestId: null,
  entries: {},
  detecting: false,
};

function setEntry(
  entries: Record<string, VerificationCacheEntry>,
  key: string,
  patch: Partial<VerificationCacheEntry>
): Record<string, VerificationCacheEntry> {
  const existing = entries[key];
  if (!existing) return entries;
  return { ...entries, [key]: { ...existing, ...patch } };
}

function mapBackendStatus(backendStatus: string | undefined): CacheVerificationStatus {
  switch (backendStatus) {
    case 'VERIFIED':
      return 'verified';
    case 'FAILED':
      return 'failed';
    case 'PENDING':
      return 'pending';
    default:
      return 'failed';
  }
}

const r = createReducer(
  initialState,

  on(autoTriggerVerifications, (state, { requestId }) => {
    if (state.requestId === requestId) {
      return { ...state, detecting: true };
    }
    return { requestId, entries: {}, detecting: true };
  }),

  on(resetVerificationCacheForRequest, (state, { requestId }) => {
    if (state.requestId !== requestId) return state;
    return initialState;
  }),

  on(triggerVerification, (state, { field }) => {
    const { key, type, panelIndex } = field;
    const existing = state.entries[key];

    if (existing?.status === 'loading') return state;

    const entry: VerificationCacheEntry = {
      key,
      type,
      panelIndex,
      status: 'loading',
      resultId: null,
      providerResponse: null,
      runOn: null,
      pollCount: 0,
      cooldownUntil: existing?.cooldownUntil ?? null,
      error: null,
      isExpired: false,
    };

    return {
      ...state,
      detecting: false,
      entries: { ...state.entries, [key]: entry },
    };
  }),

  on(hydrateVerificationResults, (state, { results }) => {
    if (!results.length) return { ...state, detecting: false };
    const entries = { ...state.entries };
    for (const { key, type, panelIndex, response, isExpired } of results) {
      const status = mapBackendStatus(response.status);
      const cooldownUntil =
        status === 'verified' || status === 'failed' ? Date.now() + VERIFICATION_REVERIFY_COOLDOWN_MS : null;
      entries[key] = {
        key,
        type,
        panelIndex,
        status,
        resultId: response.resultId ?? null,
        providerResponse: response.providerResponse ?? null,
        runOn: response.runOn ?? null,
        pollCount: 0,
        cooldownUntil,
        error: null,
        isExpired,
      };
    }
    return { ...state, detecting: false, entries };
  }),

  on(reverifyField, (state, { field }) => {
    const { key, type, panelIndex } = field;
    const existing = state.entries[key];

    const entry: VerificationCacheEntry = {
      key,
      type,
      panelIndex,
      status: 'loading',
      resultId: null,
      providerResponse: null,
      runOn: null,
      pollCount: 0,
      cooldownUntil: existing?.cooldownUntil ?? null,
      error: null,
      isExpired: false,
    };

    return {
      ...state,
      entries: { ...state.entries, [key]: entry },
    };
  }),

  on(verificationResponseReceived, (state, { key, response }) => {
    const status = mapBackendStatus(response.status);
    const cooldownUntil =
      status === 'verified' || status === 'failed' ? Date.now() + VERIFICATION_REVERIFY_COOLDOWN_MS : null;

    return {
      ...state,
      entries: setEntry(state.entries, key, {
        status,
        resultId: response.resultId ?? null,
        providerResponse: response.providerResponse ?? null,
        runOn: response.runOn ?? null,
        cooldownUntil,
        error: null,
        isExpired: false,
      }),
    };
  }),

  on(verificationFailed, (state, { key, error }) => ({
    ...state,
    entries: setEntry(state.entries, key, {
      status: 'failed',
      error,
      cooldownUntil: Date.now() + VERIFICATION_REVERIFY_COOLDOWN_MS,
      isExpired: false,
    }),
  })),

  on(pollVerification, (state, { key }) => {
    const existing = state.entries[key];
    if (!existing) return state;
    return {
      ...state,
      entries: {
        ...state.entries,
        [key]: { ...existing, pollCount: existing.pollCount + 1 },
      },
    };
  }),

  on(pollResultReceived, (state, { key, response }) => {
    const status = mapBackendStatus(response.status);
    const cooldownUntil =
      status === 'verified' || status === 'failed' ? Date.now() + VERIFICATION_REVERIFY_COOLDOWN_MS : null;

    return {
      ...state,
      entries: setEntry(state.entries, key, {
        status,
        resultId: response.resultId ?? null,
        providerResponse: response.providerResponse ?? null,
        runOn: response.runOn ?? null,
        cooldownUntil,
        error: null,
        isExpired: false,
      }),
    };
  }),

  on(pollFailed, (state, { key, error }) => ({
    ...state,
    entries: setEntry(state.entries, key, {
      status: 'failed',
      error,
      cooldownUntil: Date.now() + VERIFICATION_REVERIFY_COOLDOWN_MS,
      isExpired: false,
    }),
  })),

  on(pollTimeout, (state, { key }) => ({
    ...state,
    entries: setEntry(state.entries, key, {
      status: 'timeout',
      cooldownUntil: Date.now() + VERIFICATION_REVERIFY_COOLDOWN_MS,
      isExpired: false,
    }),
  }))
);

export function reducer(state: VerificationCacheState | undefined, action: Action) {
  return r(state, action);
}
